import React from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { Layout } from "./components/layout/Layout";
import { TourProvider } from "./contexts/TourContext";
import { AuthProvider } from "./contexts/AuthContext";
import { ProtectedRoute } from "./components/auth/ProtectedRoute";
import { LoginForm } from "./components/auth/LoginForm";
import { SignupForm } from "./components/auth/SignupForm";
import { ForgotPasswordForm, ResetPasswordForm } from "./components/auth/PasswordRecovery";
import { OAuthCallback } from "./components/auth/OAuthCallback";
import PaymentSuccess from "./pages/PaymentSuccess";
import PaymentCancelled from "./pages/PaymentCancelled";
import Dashboard from "./pages/Dashboard";
import HubSpotAcceptance from "./pages/HubSpotAcceptance";
// import Analytics from "./pages/Analytics";
// import Courses from "./pages/Courses";
// import CourseDetail from "./pages/CourseDetail";
// import ModuleDetail from "./pages/ModuleDetail";
// import AEOPulse from "./pages/AEOPulse";
// import AEOAnalyzer from "./pages/aeopulse/AEOAnalyzer";
// import ContentGenerator from "./pages/aeopulse/ContentGenerator";
// import VisualPulseAI from "./pages/aeopulse/VisualPulseAI";
// import GEOStrategyPlanner from "./pages/aeopulse/GEOStrategyPlanner";
// import InsightRank from "./pages/InsightRank";
// import RubyAIssist from "./pages/RubyAIssist";
// import ReviewLab from "./pages/ReviewLab";
// import Brandify from "./pages/Brandify";
// import PRMedia from "./pages/PRMedia";
// import ResourceVault from "./pages/ResourceVault";
import ResourceVaultV2 from "./pages/ResourceVaultV2";
import { SiteProvider } from "./components/ruby-ai-v2/contexts/SiteContext";
// import ProjectManagement from "./pages/ProjectManagement";
// import Projects from "./pages/project-management/Projects";
// import Tasks from "./pages/project-management/Tasks";
// import Documentation from "./pages/project-management/Documentation";
// import KnowledgeBase from "./pages/project-management/KnowledgeBase";
// import Ideas from "./pages/project-management/Ideas";
// import BugTracker from "./pages/project-management/BugTracker";
// import TeamManagement from "./pages/project-management/TeamManagement";
import Subscriptions from "./pages/Subscriptions";
import Settings from "./pages/Settings";
import Account from "./pages/Account";
import NotFound from "./pages/NotFound";
import Index from "./pages/Index";
// import TestingLab from "./pages/TestingLab";
import RubyAIssistV2 from "./pages/RubyAIssistV2";
import SearchIntelligenceDashboard from "./pages/SearchIntelligenceDashboard";
import BrandInsightDashboard from "./pages/BrandInsightDashboard";
import AIReadinessDashboard from "./pages/AIReadinessDashboard";
import AIVulnerabilityDashboard from "./pages/AIVulnerabilityDashboard";
import LocalIntelligenceDashboard from "./pages/LocalIntelligenceDashboard";
// import WebForgeAI from "./pages/WebForgeAI";
// import SupabaseTest from "./pages/SupabaseTest";
import TestPage from "./pages/TestPage";
import EnvTest from "./pages/EnvTest";
import OAuthTest from "./pages/OAuthTest";
// import RoleManagement from "./pages/admin/RoleManagement";
// import RBACTest from "./pages/RBACTest";
// import DatabaseTest from "./pages/DatabaseTest";
// import HubSpotTest from "./pages/HubSpotTest";
// import Security from "./pages/Security";
import "./test-hubspot"; // Import the test function to make it available
import Invite from "./pages/Invite";

// Create a new QueryClient instance outside of the component to avoid recreation on each render
const queryClient = new QueryClient();

const App = () => {
  console.log("App component loaded!");
  console.log("Environment check from App:", {
    VITE_SUPABASE_URL: import.meta.env.VITE_SUPABASE_URL,
    VITE_SUPABASE_ANON_KEY: import.meta.env.VITE_SUPABASE_ANON_KEY,
    MODE: import.meta.env.MODE,
    BASE_URL: import.meta.env.BASE_URL
  });

  return (
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <Toaster />
        <Sonner />
        <BrowserRouter>
          <AuthProvider>
            <SiteProvider>
            <TourProvider>
              <Routes>
                {/* Public routes */}
                <Route path="/" element={<Index />} />
                <Route path="/test" element={<TestPage />} />
                <Route path="/env-test" element={<EnvTest />} />
                <Route path="/oauth-test" element={<OAuthTest />} />
                {/* <Route path="/database-test" element={<DatabaseTest />} /> */}
                {/* <Route path="/hubspot-test" element={<HubSpotTest />} /> */}
                
                {/* Team invitation routes */}
                <Route path="/join-account/:token" element={<Invite />} />
                <Route path="/invite/:token" element={<Invite />} />
                
                {/* Authentication routes - should not be accessible when logged in */}
                <Route 
                  path="/auth/login" 
                  element={
                    <ProtectedRoute requireAuth={false}>
                      <LoginForm />
                    </ProtectedRoute>
                  } 
                />
                <Route 
                  path="/auth/signup" 
                  element={
                    <ProtectedRoute requireAuth={false}>
                      <SignupForm />
                    </ProtectedRoute>
                  } 
                />
                <Route path="/auth/callback" element={<OAuthCallback />} />
                <Route path="/auth/forgot-password" element={<ForgotPasswordForm />} />
                <Route path="/auth/reset-password" element={<ResetPasswordForm />} />

                {/* Public Stripe return — first-time buyers have no account yet */}
                <Route path="/payment/success" element={<PaymentSuccess />} />
                <Route path="/payment/cancelled" element={<PaymentCancelled />} />
                
                {/* Protected routes - require authentication */}
                <Route 
                  element={
                    <ProtectedRoute>
                      <Layout />
                    </ProtectedRoute>
                  } 
                >
                  <Route path="/dashboard" element={<Dashboard />} />
                  <Route path="/hubspot-acceptance" element={<HubSpotAcceptance />} />
                  <Route path="/ruby-aissist" element={<RubyAIssistV2 />} />
                  <Route path="/ruby-aissist/search-intelligence" element={<SearchIntelligenceDashboard />} />
                  <Route path="/ruby-aissist/brand-insight" element={<BrandInsightDashboard />} />
                  <Route path="/ruby-aissist/ai-readiness" element={<AIReadinessDashboard />} />
                  <Route path="/ruby-aissist/ai-vulnerability" element={<AIVulnerabilityDashboard />} />
                  <Route path="/ruby-aissist/local-intelligence" element={<LocalIntelligenceDashboard />} />
                  <Route path="/resource-vault" element={<ResourceVaultV2 />} />
                  <Route path="/subscriptions" element={<Subscriptions />} />
                  <Route path="/settings" element={<Settings />} />
                  <Route path="/account" element={<Account />} />
                  
                  {/*
                  <Route path="/dashboard" element={<Dashboard />} />
                  <Route path="/analytics" element={<Analytics />} />
                  <Route path="/courses" element={<Courses />} />
                  <Route path="/courses/:courseId" element={<CourseDetail />} />
                  <Route path="/courses/:courseId/modules/:moduleId" element={<ModuleDetail />} />
                  <Route path="/aeopulse" element={<AEOPulse />} />
                  <Route path="/aeopulse/analyzer" element={<AEOAnalyzer />} />
                  <Route path="/aeopulse/content-generator" element={<ContentGenerator />} />
                  <Route path="/aeopulse/visualpulse-ai" element={<VisualPulseAI />} />
                  <Route path="/aeopulse/geo-strategy-planner" element={<GEOStrategyPlanner />} />
                  <Route path="/insight-rank" element={<InsightRank />} />
                  <Route path="/insight-rank/website-audit" element={<InsightRank />} />
                  <Route path="/insight-rank/keyword-research" element={<InsightRank />} />
                  <Route path="/insight-rank/rank-tracking" element={<InsightRank />} />
                  <Route path="/insight-rank/backlink-analysis" element={<InsightRank />} />
                  <Route path="/insight-rank/on-page-seo" element={<InsightRank />} />
                  <Route path="/ruby-copilot" element={<RubyAIssist />} />
                  <Route path="/review-lab" element={<ReviewLab />} />
                  <Route path="/brandify" element={<Brandify />} />
                  <Route path="/pr-media" element={<PRMedia />} />
                  <Route path="/webforge-ai" element={<WebForgeAI />} />
                  <Route path="/security" element={<Security />} />
                  <Route path="/testing-lab" element={<TestingLab />} />
                  <Route path="/ruby-aissist-v2" element={<RubyAIssistV2 />} />
                  <Route path="/ruby-aissist-v2/search-intelligence" element={<SearchIntelligenceDashboard />} />
                  <Route path="/ruby-aissist-v2/brand-insight" element={<BrandInsightDashboard />} />
                  <Route path="/ruby-aissist-v2/ai-readiness" element={<AIReadinessDashboard />} />
                  <Route path="/ruby-aissist-v2/ai-vulnerability" element={<AIVulnerabilityDashboard />} />
                  <Route path="/supabase-test" element={<SupabaseTest />} />
                  <Route path="/resource-vault" element={<ResourceVault />} />
                  <Route path="/resource-vault-v2" element={<ResourceVaultV2 />} />
                  <Route path="/project-management" element={<ProjectManagement />} />
                  <Route path="/project-management/projects" element={<Projects />} />
                  <Route path="/project-management/tasks" element={<Tasks />} />
                  <Route path="/project-management/documentation" element={<Documentation />} />
                  <Route path="/project-management/knowledge-base" element={<KnowledgeBase />} />
                  <Route path="/project-management/ideas" element={<Ideas />} />
                  <Route path="/project-management/bug-tracker" element={<BugTracker />} />
                  <Route path="/project-management/team-management" element={<TeamManagement />} />
                  <Route path="/admin/role-management" element={<RoleManagement />} />
                  <Route path="/admin/rbac-test" element={<RBACTest />} />
                  */}
                </Route>
                <Route path="*" element={<NotFound />} />
              </Routes>
            </TourProvider>
            </SiteProvider>
          </AuthProvider>
        </BrowserRouter>
      </TooltipProvider>
    </QueryClientProvider>
  );
};

export default App;
